import { getInvalidFilenameSegment } from '@shared/schemas';
import config from '@config/index';

import { IGNORED_FILE_NAMES_LOWER_CASE, IGNORED_FILE_PREFIXES, ITEM_MIMETYPES } from '../constants';
import splitExtension from './splitExtension.helper';

const DOCUMENT_IMPORT_EXTENSION = 'docx';
const DOCUMENT_IMPORT_MIMETYPE = ITEM_MIMETYPES.docx[0];

export type TUploadFileWithPath = File & {
  relativePath?: string;
  path?: string;
  webkitRelativePath?: string;
};

export const getUploadRelativePath = (file: TUploadFileWithPath): string => {
  const path = file.webkitRelativePath || file.relativePath || file.path || file.name;
  return path.replace(/^(?:\.\/|\/)+/, '');
};

export const isIgnoredFile = (fileName: string): boolean => {
  const name = fileName.split('/').pop() || fileName;
  return (
    IGNORED_FILE_PREFIXES.some((prefix) => name.startsWith(prefix)) ||
    IGNORED_FILE_NAMES_LOWER_CASE.includes(name.toLowerCase())
  );
};

export const isDocumentImportFile = (file: File, relPath?: string): boolean => {
  const [, ext] = splitExtension(relPath || file.name);
  return ext.toLowerCase() === DOCUMENT_IMPORT_EXTENSION || file.type === DOCUMENT_IMPORT_MIMETYPE;
};

export type TValidationError = { type: 'error' | 'info'; message: string } | null;

export const validateUploadPermissions = (
  isBlockFolderType: boolean,
  countriesFolderType: boolean,
  snapshotsFolderType: boolean,
  accessRight: number,
  userGroups: string[],
  editAccess: number,
  authorGroup: string
): TValidationError => {
  if (isBlockFolderType || countriesFolderType || snapshotsFolderType) {
    return { type: 'error', message: 'You cannot upload files to this folder.' };
  }
  if (accessRight < editAccess) {
    return { type: 'error', message: 'You do not have edit permission.' };
  }
  if (!userGroups.includes(authorGroup)) {
    return { type: 'error', message: 'You are not a member of the Author group.' };
  }
  return null;
};

export const validateFilenames = (relPaths: string[]): TValidationError => {
  const invalidSegment = relPaths.map(getInvalidFilenameSegment).find((s) => s !== null);
  if (invalidSegment) {
    return { type: 'error', message: `Invalid folder or file name: "${invalidSegment}".` };
  }
  return null;
};

export const validateImportRestriction = (
  files: File[],
  fileNameRegex?: string,
  whitelistedMails?: string[],
  userMail?: string,
  customMessage?: string
): TValidationError => {
  if (!fileNameRegex || whitelistedMails?.includes(userMail || '')) return null;

  const regex = new RegExp(fileNameRegex);
  const invalidFile = files.find((f) => !regex.test(f.name));
  if (invalidFile) {
    return { type: 'error', message: customMessage ?? 'File upload is not enabled for your demo organization.' };
  }
  return null;
};

export const validateFileSizes = (
  files: File[],
  relPathsOrMaxSizeMb: string[] | number = [],
  isStaticFiles = false
): TValidationError => {
  if (typeof relPathsOrMaxSizeMb === 'number') {
    const maxSizeMb = relPathsOrMaxSizeMb;
    const maxSize = maxSizeMb * 1024 * 1024;
    const oversizedFile = files.find((f) => f.size > maxSize);
    if (oversizedFile) {
      return {
        type: 'error',
        message: `${oversizedFile.name} is too large. Maximum file size is ${maxSizeMb}MB.`,
      };
    }
    return null;
  }

  const relPaths = relPathsOrMaxSizeMb;
  for (const [index, file] of files.entries()) {
    const isDocumentImport = !isStaticFiles && isDocumentImportFile(file, relPaths[index]);
    const maxSizeMb = isDocumentImport
      ? config.MAX_FILE_SIZES_MB.DOCUMENT_IMPORT_FILE
      : config.MAX_FILE_SIZES_MB.IMPORT_FILE;

    if (file.size <= maxSizeMb * 1024 * 1024) continue;

    return {
      type: 'error',
      message: isDocumentImport
        ? `${file.name} is too large. Maximum Word document import file size is ${maxSizeMb}MB.`
        : `${file.name} is too large. Maximum file size is ${maxSizeMb}MB.`,
    };
  }
  return null;
};

export const validateFileCount = (count: number): TValidationError => {
  if (count > config.MAX_FILES_UPLOAD) {
    return { type: 'error', message: `You cannot upload more than ${config.MAX_FILES_UPLOAD} files at once.` };
  }
  return null;
};

export const validateTotalSize = (
  files: File[],
  maxTotalMb: number = config.MAX_TOTAL_UPLOAD_SIZE
): TValidationError => {
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  const maxTotal = maxTotalMb * 1024 * 1024;
  if (totalSize > maxTotal) {
    return { type: 'error', message: `Total upload size cannot exceed ${maxTotalMb}MB.` };
  }
  return null;
};

export type TFilterResult = {
  importableFiles: File[];
  importableRelPaths: string[];
  ignoredCount: number;
};

export const filterIgnoredFiles = (files: File[], relPaths?: string[]): TFilterResult => {
  const paths = relPaths ?? files.map((f) => f.name);
  const ignoredIndices = files.map((f, i) => (isIgnoredFile(f.name) ? i : -1)).filter((i) => i !== -1);
  const importableFiles = files.filter((_, i) => !ignoredIndices.includes(i));
  const importableRelPaths = paths.filter((_, i) => !ignoredIndices.includes(i));
  return {
    importableFiles,
    importableRelPaths,
    ignoredCount: ignoredIndices.length,
  };
};
